import { z } from "zod/v4";

/** Self-serve skill budget: post-confirm changes before the pick locks. */
export const MAX_SKILL_CHANGES = 2;

/**
 * Self-serve gender budget. Mirrors migration 271, which caps
 * `users.gender_changes_remaining` at 1 with a CHECK constraint - support can
 * hand back at most that one free change.
 */
export const MAX_GENDER_CHANGES = 1;

/**
 * Cap on the support note stored with a ban. Mirrors `maxBanReasonLen` in
 * api/go/identity/internal/admin/handler_ban.go - the server rejects anything
 * longer with a 400, so the client has to hold the same number.
 */
export const MAX_BAN_REASON_LEN = 500;

/**
 * Length of a ban reason as the server measures it.
 *
 * `String.length` counts UTF-16 units, so every character above the BMP - an
 * emoji in a support note - counts twice, and the client would refuse a reason
 * the API accepts. Spreading the string iterates code points, which is what
 * `utf8.RuneCountInString` counts on the other side.
 */
export function banReasonLength(reason: string): number {
	return [...reason].length;
}

/**
 * Clips a ban reason to the cap without splitting a character.
 *
 * `slice` cuts at a UTF-16 index, which can land between the two halves of a
 * surrogate pair and leave a lone surrogate that no longer decodes.
 */
export function clipBanReason(reason: string): string {
	return [...reason].slice(0, MAX_BAN_REASON_LEN).join("");
}

export const skillLevelSchema = z.enum([
	"novice",
	"beginner",
	"intermediate",
	"skilled",
	"advanced",
	"elite",
	"pro",
]);
export const userGenderSchema = z.enum(["male", "female", "other"]);
export const gameStatusSchema = z.enum([
	"draft",
	"open",
	"full",
	"in_progress",
	"completed",
	"cancelled",
]);
export const participantStatusSchema = z.enum(["confirmed", "waitlisted", "declined"]);
export const matchStatusSchema = z.enum(["in_progress", "pending_verification", "completed"]);
export const winningSideSchema = z.enum(["team1", "team2"]);
export const clubRoleSchema = z.enum(["owner", "admin", "trainer", "member", "organizer"]);
export const membershipStatusSchema = z.enum([
	"pending",
	"invited",
	"active",
	"paused",
	"cancelled",
	"declined",
]);
export const moderationStatusSchema = z.enum(["approved", "pending_review", "rejected"]);
export const communityTypeSchema = z.enum(["academy", "club", "group", "team", "squad"]);
// The effective verification badge on a community. The API writes "" rather
// than omitting the field when no badge is active.
export const communityBadgeTierSchema = z.enum(["", "blue", "gold"]);
export const joinPolicySchema = z.enum(["request", "invite_only", "full", "member"]);
export const subscriptionTierSchema = z.enum(["free", "club", "club_pro", "verband"]);
export const memberTypeSchema = z.enum([
	"member",
	"trainer",
	"youth_coordinator",
	"treasurer",
	"secretary",
	"board_member",
	"honorary",
	"passive",
]);
